// 行业详情页（/industry/:id）。原先内联在 MarketObservation.jsx。
import React from "react";
import { Database, RefreshCw } from "lucide-react";

import { getLiveInstrumentIntraday, getMarketIndustryDetail } from "../../api/client";
import { Chart } from "../../components/charts";
import { BreadthMini } from "./MarketAtoms";
import { IndustryMemberTable } from "./IndustryMemberTable";
import { InstrumentComparePicker } from "./InstrumentComparePicker";
import { ErrorState, EvidenceFooter, LoadingState, PageHeading, Unavailable } from "./MarketScaffold";
import { instrumentKlineOption, marketSeriesChartOption } from "./chartOptions";
import { integer, number, signed, toneClass } from "./marketFormat";
import { useIndustrySeriesWithLiveFallback } from "./useIndustrySeriesWithLiveFallback";
import { useLatestRequest } from "./useLatestRequest";
import baseStyles from "./marketObservation.module.css";
import overviewStyles from "./marketOverview.module.css";
import tableStyles from "./marketTables.module.css";

const styles = { ...baseStyles, ...overviewStyles, ...tableStyles };

const PERIODS = [
  ["intraday", "分时"],
  ["daily", "日K"],
  ["weekly", "周K"],
  ["monthly", "月K"],
  ["yearly", "年K"],
];

export function IndustryDetailPage({ industryCode, navigate, onStatus, snapshotId }) {
  React.useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [industryCode]);
  const [period, setPeriod] = React.useState("daily");
  const [payload, setPayload] = React.useState(null);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [selectedComparisons, setSelectedComparisons] = React.useState([]);
  const [seriesRefreshKey, setSeriesRefreshKey] = React.useState(0);
  const [memberQuery, setMemberQuery] = React.useState("");
  const [memberSort, setMemberSort] = React.useState("pct_chg");
  const [memberOrder, setMemberOrder] = React.useState("desc");
  const [page, setPage] = React.useState(1);
  const requestGuard = useLatestRequest();
  const load = React.useCallback(async () => {
    const requestVersion = requestGuard.start();
    setLoading(true);
    setError("");
    try {
      const data = await getMarketIndustryDetail(industryCode, { period: "daily", snapshotId });
      if (!requestGuard.isCurrent(requestVersion)) return;
      setPayload(data);
      onStatus({ status: data.status, dataDate: data.data_date });
    } catch (requestError) {
      if (!requestGuard.isCurrent(requestVersion)) return;
      setError(requestError.message);
      onStatus({ status: "unavailable", dataDate: null });
    } finally {
      if (requestGuard.isCurrent(requestVersion)) setLoading(false);
    }
  }, [industryCode, onStatus, requestGuard, snapshotId]);
  React.useEffect(() => { void load(); }, [load]);
  const previousClose = Number(payload?.industry?.index_close);
  const [liveIndex, setLiveIndex] = React.useState(null);
  React.useEffect(() => {
    const industryCodeForLive = payload?.industry?.industry_code;
    setLiveIndex(null);
    if (!industryCodeForLive || !Number.isFinite(previousClose) || previousClose === 0) return undefined;
    let active = true;
    (async () => {
      try {
        const live = await getLiveInstrumentIntraday({
          code: industryCodeForLive,
          kind: "industry",
          previousClose,
        });
        const livePoints = live?.data?.series?.[0]?.points;
        const level = Number(livePoints?.[livePoints.length - 1]?.raw);
        const liveTradeDate = live?.meta?.trade_date;
        const publishedDataDate = payload?.data_date;
        const liveIsNewer = liveTradeDate && publishedDataDate && liveTradeDate > publishedDataDate;
        if (active && liveIsNewer && Number.isFinite(level)) {
          setLiveIndex({ close: level, pct_chg: (level / previousClose - 1) * 100 });
        }
      } catch {
        // keep the EOD index value if the live feed is unavailable
      }
    })();
    return () => {
      active = false;
    };
  }, [payload?.data_date, payload?.industry?.industry_code, previousClose]);
  const { seriesPayload, seriesLoading, seriesError } = useIndustrySeriesWithLiveFallback({
    industryId: payload?.industry?.industry_id,
    industryCode: payload?.industry?.industry_code,
    period,
    selectedComparisons,
    snapshotId,
    refreshKey: seriesRefreshKey,
    previousClose,
  });
  React.useEffect(() => { setPage(1); }, [memberQuery, memberSort, memberOrder, industryCode]);
  React.useEffect(() => {
    setPeriod("daily");
    setSelectedComparisons([]);
  }, [industryCode]);
  function updateComparisons(items) {
    if (selectedComparisons.length >= 2 && items.length > selectedComparisons.length) return;
    setSelectedComparisons(items.slice(0, 2));
  }

  if (!payload && loading) return <LoadingState label="正在读取行业详情" />;
  if (!payload && error) return <ErrorState error={error} onRetry={load} />;
  if (!payload) return null;
  const industry = payload.industry;
  const derivedCloseOnly = industry.index_data_status === "derived"
    && industry.index_bar_status === "close_only";
  const derivedProxyOhlc = industry.index_data_status === "derived"
    && industry.index_bar_status === "proxy_ohlc_1m";
  const normalizedQuery = memberQuery.trim().toLowerCase();
  const filteredMembers = payload.members
    .filter((item) => !normalizedQuery || `${item.name ?? ""}${item.code}${item.l2_industry ?? ""}`.toLowerCase().includes(normalizedQuery))
    .sort((a, b) => {
      const left = memberSort === "name" ? `${a.name ?? ""}${a.code}` : Number(a[memberSort] ?? Number.NEGATIVE_INFINITY);
      const right = memberSort === "name" ? `${b.name ?? ""}${b.code}` : Number(b[memberSort] ?? Number.NEGATIVE_INFINITY);
      if (typeof left === "string") return memberOrder === "asc" ? left.localeCompare(right, "zh-CN") : right.localeCompare(left, "zh-CN");
      return memberOrder === "asc" ? left - right : right - left;
    });
  const pageSize = 50;
  const pageCount = Math.max(1, Math.ceil(filteredMembers.length / pageSize));
  const visibleMembers = filteredMembers.slice((page - 1) * pageSize, page * pageSize);
  const seriesMethods = (seriesPayload?.data?.series ?? [])
    .map((item) => item.method)
    .filter((item, index, items) => item && items.indexOf(item) === index);
  const partialIntraday = period === "intraday"
    && seriesPayload?.meta?.series_status === "partial_intraday";

  return (
    <div className={styles.page}>
      <PageHeading
        eyebrow={`${industry.industry_level} · ${industry.industry_code}`}
        title={industry.industry_name}
        description={derivedCloseOnly
          ? "申万官方日线尚未发布；当前展示成分股加权派生收盘，不补造开高低"
          : derivedProxyOhlc
            ? "申万官方日线尚未发布；当前展示成分股加权分钟代理 K 线"
          : industry.series_method === "official_index_daily"
            ? "申万行业指数与成分股表现"
            : "基于真实成分股聚合的行业表现"}
        status={payload.status}
        dataDate={payload.data_date}
        backAction={() => navigate("/industry")}
      />

      <section className={styles.industryStatRow}>
        <div>
          <span>
            行业指数
            {liveIndex
              ? <span className={styles.metricTag}>实时</span>
              : derivedCloseOnly
                ? <span className={styles.metricTag}>暂估收盘</span>
                : derivedProxyOhlc
                  ? <span className={styles.metricTag}>代理指数</span>
                : null}
          </span>
          <div className={styles.industryStatValue}>
            <strong>
              {liveIndex ? number(liveIndex.close) : number(industry.index_close)}
            </strong>
            <span className={toneClass(liveIndex ? liveIndex.pct_chg : industry.pct_chg, styles)}>
              {signed(liveIndex ? liveIndex.pct_chg : industry.pct_chg)}
            </span>
          </div>
        </div>
        <div><span>成分股数</span><strong>{integer(payload.member_coverage.total_members)}</strong></div>
        <div><span>成交额(亿)</span><strong>{number(industry.turnover_yi, 1)}</strong></div>
        <div>
          <span>涨跌分布</span>
          <BreadthMini item={{ rise: industry.rise, flat: industry.flat, fall: industry.fall }} styles={styles} />
          <div className={styles.industryStatBreadthCounts}>
            <span className={styles.up}>↑{integer(industry.rise)}</span>
            <span className={styles.down}>↓{integer(industry.fall)}</span>
            <span>—{integer(industry.flat)}</span>
          </div>
        </div>
      </section>

      <section className={styles.card}>
        <div className={styles.detailChartToolbar}>
          <div className={styles.periodTabs}>
            {PERIODS.map(([value, label]) => (
              <button key={value} className={period === value ? styles.active : ""} aria-pressed={period === value} onClick={() => setPeriod(value)}>{label}</button>
            ))}
            {period === "intraday" && (
              <button
                type="button"
                aria-label="刷新分时"
                disabled={seriesLoading}
                onClick={() => setSeriesRefreshKey((value) => value + 1)}
              >
                <RefreshCw size={13} className={seriesLoading ? styles.spinning : ""} />刷新分时
              </button>
            )}
          </div>
          <div className={styles.compareControl}>
            <InstrumentComparePicker
              snapshotId={snapshotId}
              currentInstrumentId={industry.industry_id}
              selected={selectedComparisons}
              onChange={updateComparisons}
              maxTotal={3}
            />
          </div>
        </div>
        {seriesError ? <Unavailable reason={seriesError} /> : seriesPayload ? (
          <div className={`${styles.detailChart} ${seriesLoading ? styles.chartRefreshing : ""}`} aria-busy={seriesLoading}>
            <Chart option={period === "intraday" ? marketSeriesChartOption(seriesPayload, period, previousClose) : instrumentKlineOption(seriesPayload, period)} />
          </div>
        ) : (
          <LoadingState label="正在读取已发布序列" />
        )}
        <div className={styles.chartEvidence}>
          <Database size={14} />
          图表口径：{selectedComparisons.length ? "共同起点 100 归一化对比" : "已发布原始序列"}
          {seriesMethods.length ? ` · ${seriesMethods.join(" / ")}` : ""}
          {partialIntraday && (
            <span role="status">
              盘中部分分时 · 已取得 {integer(seriesPayload.meta.point_count)} 个采样点，仅展示已覆盖时段
            </span>
          )}
          {seriesLoading && <span role="status">图表数据更新中</span>}
        </div>
      </section>

      <IndustryMemberTable
        filteredCount={filteredMembers.length}
        memberOrder={memberOrder}
        memberQuery={memberQuery}
        memberSort={memberSort}
        navigate={navigate}
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        returnTo={`/industry/${industryCode}`}
        setMemberOrder={setMemberOrder}
        setMemberQuery={setMemberQuery}
        setMemberSort={setMemberSort}
        setPage={setPage}
        visibleMembers={visibleMembers}
      />
      <EvidenceFooter payload={payload} />
    </div>
  );
}
